import {
  createAsyncThunk,
  createSlice
} from '@reduxjs/toolkit';

// Utils
import { supabase } from 'utils/supabase-client';
import normalizeEmail from 'utils/normalize-email';


// Initial State
const initialState = {
  authenticationInitialized: false,
  displaySessionExpiredMessage: false,
  email: null,
  familyName: null,
  givenName: null,
  hasRestaurant: false,
  id: null,
  isFetchingUser: false,
  isLoggingIn: false,
  isLoggingOut: false,
  isRegistering: false,
  isUpdating: false,
  photo: null
};


const getProfileData = async (userId) => {
  // Query the profiles table using the user ID
  const { data: profile } = await supabase
    .from('profiles')
    .select(`
      familyName:family_name,
      givenName:given_name,
      photo,
      locale
    `)
    .eq('id', userId)
    .single();

  // Check if the user has any restaurants
  const { count } = await supabase
    .from('restaurants')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId);

  return {
    ...profile,
    hasRestaurant: !!count
  };
};

// Thunks
const login = createAsyncThunk(
  'user/login',
  async (data, { rejectWithValue }) => {
    const {
      email,
      password
    } = data || {};

    const normalizedEmail = normalizeEmail(email);

    try {
      const response = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password
      });

      const {
        data: { user },
        error
      } = response;


      if (error) {
        throw error;
      }

      if (!user) {
        throw new Error('User not authenticated');
      }

      const profileData = await getProfileData(user.id);

      // Return both profile and auth data
      return {
        ...profileData,
        email: user.email,
        id: user.id
      };
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

const logout = createAsyncThunk(
  'user/logout',
  async (_, { rejectWithValue }) => {
    try {
      const response = await supabase.auth.signOut();

      const { error } = response;

      if (error) {
        throw error;
      }

      return response;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

const register = createAsyncThunk(
  'user/register',
  async (data, { rejectWithValue }) => {
    const {
      email,
      familyName,
      givenName,
      password
    } = data;

    const normalizedEmail = normalizeEmail(email);

    try {
      const { user, error } = await supabase.auth.signUp({
        email: normalizedEmail,
        options: {
          data: {
            /* eslint-disable camelcase */
            family_name: familyName,
            given_name: givenName
            /* eslint-enable camelcase */
          }
        },
        password
      });

      if (error) {
        throw error;
      }

      return user;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

const syncSession = createAsyncThunk(
  'user/syncSession',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await supabase.auth.getUser();

      const { user } = data || {};

      if (!user) {
        throw new Error('User not authenticated');
      }

      const profileData = await getProfileData(user.id);

      // Return both profile and auth data
      return {
        ...profileData,
        email: user.email,
        id: user.id
      };
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

const updateUser = createAsyncThunk(
  'user/updateUser',
  async (data, { getState, rejectWithValue }) => {
    const {
      familyName,
      givenName,
      locale
    } = data || {};

    const dataToUpdate = {};

    /* eslint-disable */
    /* eslint-disable camelcase */
    if (familyName !== undefined) dataToUpdate.family_name = familyName;
    if (givenName !== undefined) dataToUpdate.given_name = givenName;
    /* eslint-enable camelcase */
    if (locale !== undefined) dataToUpdate.locale = locale;
    /* eslint-enable */

    try {
      const userId = getState().user.id;

      if (!userId) {
        throw new Error('User ID not found');
      }

      const {
        data: updatedData,
        error
      } = await supabase
        .from('profiles')
        .update(dataToUpdate)
        .eq('id', userId)
        .select(`
          familyName:family_name,
          givenName:given_name,
          locale
        `)
        .single();

      if (error) {
        throw error;
      }

      return updatedData;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

// Slice
const userSlice = createSlice({
  extraReducers: (builder) => {
    builder
      // Login Handlers
      .addCase(login.pending, (state) => {
        state.isLoggingIn = true;
      })
      .addCase(login.fulfilled, (state, { payload }) => {
        if (payload) userSlice.caseReducers.setUserData(state, { payload });

        state.authenticationInitialized = true;
        state.isLoggingIn = false;
      })
      .addCase(login.rejected, (state) => {
        state.authenticationInitialized = true;
        state.isLoggingIn = false;
      })
      // Sign Up Handlers
      .addCase(register.pending, (state) => {
        state.isRegistering = true;
      })
      .addCase(register.fulfilled, (state, { payload }) => {
        state.isRegistering = false;

        if (payload) userSlice.caseReducers.setUserData(state, { payload });
      })
      .addCase(register.rejected, (state) => {
        state.isRegistering = false;
      })
      // Logout Handlers
      .addCase(logout.pending, (state) => {
        state.isLoggingOut = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoggingOut = false;

        userSlice.caseReducers.clearUserData(state, {});
      })
      .addCase(logout.rejected, (state) => {
        state.isLoggingOut = false;
      })
      // Sync Session Handlers
      .addCase(syncSession.pending, (state) => {
        state.isLoggingIn = true;
      })
      .addCase(syncSession.fulfilled, (state, { payload }) => {
        if (payload) userSlice.caseReducers.setUserData(state, { payload });

        state.authenticationInitialized = true;
        state.isLoggingIn = false;
      })
      .addCase(syncSession.rejected, (state) => {
        userSlice.caseReducers.clearUserData(state, {});

        state.isLoggingIn = false;
        state.authenticationInitialized = true;
      })
      // Update User Handlers
      .addCase(updateUser.pending, (state) => {
        state.isUpdating = true;
      })
      .addCase(updateUser.fulfilled, (state, { payload }) => {
        state.isUpdating = false;

        if (payload) userSlice.caseReducers.setUserData(state, { payload });
      })
      .addCase(updateUser.rejected, (state) => {
        state.isUpdating = false;
      });
  },

  initialState,

  name: 'user',

  reducers: {
    clearUserData: (state, { payload }) => {
      const { displaySessionExpiredMessage } = payload || {};

      return {
        ...initialState,
        authenticationInitialized: state.authenticationInitialized,
        displaySessionExpiredMessage
      };
    },
    disableDisplaySessionExpiredMessage: (state) => {
      state.displaySessionExpiredMessage = false;
    },
    setUserData: (state, { payload }) => {
      const {
        email,
        familyName,
        givenName,
        hasRestaurant,
        locale,
        photo,
        id
      } = payload || {};


      if (email) state.email = email;
      if (id) state.id = id;
      if (typeof hasRestaurant === 'boolean') state.hasRestaurant = hasRestaurant;
      if (locale) state.locale = locale;
      if (familyName) state.familyName = familyName;
      if (givenName) state.givenName = givenName;
      if (photo) state.photo = photo;
    }
  }
});

// Actions
const {
  clearUserData,
  disableDisplaySessionExpiredMessage,
  setUserData
} = userSlice.actions;

// Selectors
const selectAuthenticationInitialized = state => state.user.authenticationInitialized;

const selectEmail = state => state.user.email;

const selectUserId = state => state.user.id;

const selectDisplaySessionExpiredMessage = state => state.user.displaySessionExpiredMessage;

const selectHasRestaurant = state => state.user.hasRestaurant;

const selectIsLoggingIn = state => state.user.isLoggingIn;

const selectIsRegistering = state => state.user.isRegistering;

const selectIsUpdating = state => state.user.isUpdating;

const selectLocale = state => state.user.locale;

const selectFamilyName = state => state.user.familyName;

const selectGivenName = state => state.user.givenName;

const selectPhoto = state => state.user.photo;

const selectUser = state => state.user;

const selectUserIsAuthenticated = state => !!state.user.email;

export {
  clearUserData,
  disableDisplaySessionExpiredMessage,
  login,
  logout,
  register,
  selectAuthenticationInitialized,
  selectDisplaySessionExpiredMessage,
  selectEmail,
  selectFamilyName,
  selectGivenName,
  selectHasRestaurant,
  selectIsLoggingIn,
  selectIsRegistering,
  selectIsUpdating,
  selectLocale,
  selectPhoto,
  selectUser,
  selectUserIsAuthenticated,
  selectUserId,
  setUserData,
  syncSession,
  updateUser
};

export default userSlice.reducer;
